/**
 * CEPI trace summary cell formatters — Admin User Activity (P0-T4).
 *
 * These render the inline columns added to the Recent Actions table in
 * ``AdminPage``. Each accepts the (possibly null) ``cepi_trace_summary``
 * JSON blob and returns a string suitable for the table cell — falling
 * back to ``TRACE_CELL_FALLBACK`` ("—") for any row that lacks a trace
 * (non-CEPI actions, or CEPI requests that completed with
 * ``CEPI_TRACE_ENABLED=False``).
 *
 * The trace argument is typed as ``any`` to match how ``AdminPage``
 * receives it from the admin API (a free-form JSON blob attached to
 * activity rows). The expected shape is documented inline below.
 */

export const TRACE_CELL_FALLBACK = '—';

/**
 * Return a comma-separated list of tool names called during the CEPI
 * turn, or the fallback when the trace is null / has no tool calls /
 * has only malformed tool-call entries.
 */
export function formatToolNames(trace: any): string {
  if (!trace || !Array.isArray(trace.tool_calls)) return TRACE_CELL_FALLBACK;
  const names = trace.tool_calls
    .map((tc: any) => (tc && typeof tc.name === 'string' ? tc.name : null))
    .filter((n: string | null): n is string => n !== null);
  if (names.length === 0) return TRACE_CELL_FALLBACK;
  return names.join(', ');
}

/**
 * Sum ``total_token_count`` across the trace's ``llm_calls`` and
 * locale-format the result. A trace with an ``llm_calls`` array that
 * exists but happens to sum to zero (every call missing the field, or
 * legitimately zero) returns ``"0"`` — that is meaningfully different
 * from "no LLM calls at all" (which returns the fallback). Negative
 * sums can't legitimately occur and fall through to the fallback
 * defensively.
 */
export function formatTotalTokens(trace: any): string {
  if (!trace || !Array.isArray(trace.llm_calls) || trace.llm_calls.length === 0) {
    return TRACE_CELL_FALLBACK;
  }
  const total = trace.llm_calls.reduce((sum: number, call: any) => {
    const v = call?.total_token_count;
    return sum + (typeof v === 'number' ? v : 0);
  }, 0);
  if (total < 0) return TRACE_CELL_FALLBACK;
  return total.toLocaleString();
}

/**
 * Render ``latency_ms`` as decimal seconds (>=1000ms) or integer
 * milliseconds (<1000ms). Missing, non-numeric, or negative values
 * return the fallback.
 */
export function formatLatency(trace: any): string {
  if (!trace || typeof trace.latency_ms !== 'number') return TRACE_CELL_FALLBACK;
  const ms = trace.latency_ms;
  if (ms < 0) return TRACE_CELL_FALLBACK;
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
}
